// Error handling utilities for Digital ID App
// Centralized error management and logging

// Error types
export const ErrorTypes = {
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  DATABASE: 'DATABASE_ERROR',
  NETWORK: 'NETWORK_ERROR',
  SHARE: 'SHARE_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Error severity levels
export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Custom error class
export class AppError extends Error {
  constructor(message, type = ErrorTypes.UNKNOWN, severity = ErrorSeverity.MEDIUM, details = null) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.severity = severity;
    this.details = details;
    this.timestamp = new Date().toISOString();
    
    // Maintain proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

// Error logger
export const logError = (error, context = {}) => {
  const errorLog = {
    message: error.message,
    type: error.type || ErrorTypes.UNKNOWN,
    severity: error.severity || ErrorSeverity.MEDIUM,
    timestamp: error.timestamp || new Date().toISOString(),
    stack: error.stack,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('App Error:', errorLog);
  }
  
  // Send error to server for logging (in production)
  try {
    if (process.env.NODE_ENV === 'production') {
      // Could implement server-side error logging here
      // fetch('/api/errors', { method: 'POST', body: JSON.stringify(errorLog) });
    }
  } catch (e) {
    console.warn('Could not send error to server:', e);
  }
  
  return errorLog;
};

// Handle validation errors
export const handleValidationError = (errors, context = 'Form validation') => {
  const errorMessages = Object.entries(errors).map(([field, message]) => `${field}: ${message}`);
  const error = new AppError(
    `Validation failed: ${errorMessages.join(', ')}`,
    ErrorTypes.VALIDATION,
    ErrorSeverity.LOW,
    { fields: errors }
  );
  
  logError(error, { context });
  return error;
};

// Handle authentication errors
export const handleAuthError = (message, details = null) => {
  const error = new AppError(
    message,
    ErrorTypes.AUTHENTICATION,
    ErrorSeverity.HIGH,
    details
  );
  
  logError(error, { action: 'authentication' });
  return error;
};

// Handle database errors
export const handleDatabaseError = (message, operation = 'unknown', details = null) => {
  const error = new AppError(
    `Database error during ${operation}: ${message}`,
    ErrorTypes.DATABASE,
    ErrorSeverity.HIGH,
    details
  );
  
  logError(error, { operation });
  return error;
};

// Handle share errors
export const handleShareError = (message, shareType = 'unknown', details = null) => {
  const error = new AppError(
    `Share error (${shareType}): ${message}`,
    ErrorTypes.SHARE,
    ErrorSeverity.MEDIUM,
    details
  );
  
  logError(error, { shareType });
  return error;
};

// Handle network errors
export const handleNetworkError = (message, url = '', details = null) => {
  const error = new AppError(
    `Network error: ${message}`,
    ErrorTypes.NETWORK,
    ErrorSeverity.MEDIUM,
    { url, ...details }
  );
  
  logError(error, { url });
  return error;
};

// Generic error handler
export const handleGenericError = (error, context = {}) => {
  let appError;
  
  if (error instanceof AppError) {
    appError = error;
  } else {
    appError = new AppError(
      error.message || 'An unexpected error occurred',
      ErrorTypes.UNKNOWN,
      ErrorSeverity.MEDIUM,
      { originalError: error.toString() }
    );
  }
  
  logError(appError, context);
  return appError;
};

// Error boundary helper
export const createErrorBoundaryHandler = (componentName) => {
  return (error, errorInfo) => {
    const appError = new AppError(
      `React error in ${componentName}: ${error.message}`,
      ErrorTypes.UNKNOWN,
      ErrorSeverity.HIGH,
      { componentStack: errorInfo.componentStack }
    );
    
    logError(appError, { component: componentName });
    return appError;
  };
};

// User-friendly error messages
export const getUserFriendlyMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  switch (error.type) {
    case ErrorTypes.VALIDATION:
      return 'Please check your input and try again';
    
    case ErrorTypes.AUTHENTICATION:
      return 'Authentication failed. Please check your credentials';
    
    case ErrorTypes.AUTHORIZATION:
      return 'You don\'t have permission to perform this action';
    
    case ErrorTypes.DATABASE:
      return 'Unable to save your data. Please try again';
    
    case ErrorTypes.NETWORK:
      return 'Network error. Please check your connection and try again';
    
    case ErrorTypes.SHARE:
      return 'Unable to create share link. Please try again';
    
    default:
      return error.message || 'Something went wrong. Please try again';
  }
};

// Get error history for debugging (removed localStorage - implement server-side if needed)
export const getErrorHistory = () => {
  console.warn('Error history is not available - implement server-side logging if needed');
  return [];
};

// Clear error history (removed localStorage - implement server-side if needed)
export const clearErrorHistory = () => {
  console.warn('Clear error history is not available - implement server-side logging if needed');
  return true;
};

// Check if error should be retried
export const shouldRetryError = (error) => {
  if (!error || !error.type) return false;
  
  // Retry network and database errors, but not authentication or validation errors
  return [ErrorTypes.NETWORK, ErrorTypes.DATABASE].includes(error.type);
};

// Create retry handler
export const createRetryHandler = (operation, maxRetries = 3, delay = 1000) => {
  return async (...args) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation(...args);
      } catch (error) {
        lastError = error;
        
        // Don't retry if error type shouldn't be retried
        if (!shouldRetryError(error)) {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
    
    throw lastError;
  };
};

// Safe operation wrapper
export const safeOperation = async (operation, errorHandler = null) => {
  try {
    return await operation();
  } catch (error) {
    const appError = handleGenericError(error);
    
    if (errorHandler) {
      return errorHandler(appError);
    }
    
    throw appError;
  }
};

// Development helpers
export const debugError = (error) => {
  if (process.env.NODE_ENV === 'development') {
    console.groupEnd();
  }
};

export const getErrorStats = () => {
  const history = getErrorHistory();
  const stats = {
    total: history.length,
    byType: {},
    bySeverity: {},
    recent: history.slice(0, 10)
  };
  
  history.forEach(error => {
    stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
    stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
  });
  
  return stats;
};
